// src/pages/Soporte.js
import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { FaPhoneAlt, FaEnvelope, FaClock, FaCheckCircle, FaLifeRing, FaArrowLeft } from 'react-icons/fa';
import { useNotification } from '../context/NotificationContext';
import { useAuth } from '../context/AuthContext';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase';
import './AyudaSoporte.css';

const categories = [
    'Pagos y facturación',
    'Acceso y visitas',
    'Amenidades y reservas',
    'Comunicados y votaciones',
    'Administración del fraccionamiento',
    'Soporte técnico',
];

const Soporte = () => {
    const { showNotification } = useNotification();
    const { currentUser, userData } = useAuth();

    const initialNombre = useMemo(() => {
        const p = userData?.portalNombrePartes || {};
        const parts = [p?.nombre, p?.apellidoPaterno, p?.apellidoMaterno]
            .filter((x) => typeof x === 'string' && x.trim().length > 0);
        if (parts.length) return parts.join(' ').trim();
        return userData?.portalNombre || userData?.nombreCompleto || currentUser?.displayName || '';
    }, [currentUser?.displayName, userData]);

    const initialTelefono = useMemo(() => {
        return userData?.portalTelefono || userData?.telefono || userData?.celular || '';
    }, [userData]);

    const initialCorreo = useMemo(() => currentUser?.email || '', [currentUser?.email]);
    const initialFracc = useMemo(() => userData?.fraccionamientoNombre || '', [userData?.fraccionamientoNombre]);

    const [form, setForm] = useState({
        nombre: initialNombre,
        correo: initialCorreo,
        telefono: initialTelefono,
        fraccionamiento: initialFracc,
        categoria: '',
        asunto: '',
        mensaje: '',
    });

    useEffect(() => {
        setForm((prev) => ({
            ...prev,
            nombre: initialNombre,
            correo: initialCorreo,
            telefono: initialTelefono,
            fraccionamiento: initialFracc,
        }));
    }, [initialNombre, initialCorreo, initialTelefono, initialFracc]);
    const [submitting, setSubmitting] = useState(false);

    const handleChange = (field) => (event) => {
        setForm((prev) => ({ ...prev, [field]: event.target.value }));
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        if (!form.nombre || !form.correo || !form.categoria || !form.asunto || !form.mensaje) {
            showNotification('Por favor completa los campos obligatorios.', 'error');
            return;
        }
        try {
            setSubmitting(true);
            const sendSupport = httpsCallable(functions, 'sendSupportRequest');
            const res = await sendSupport({
                nombre: form.nombre,
                correo: form.correo,
                telefono: form.telefono,
                fraccionamiento: form.fraccionamiento,
                categoria: form.categoria,
                asunto: form.asunto,
                mensaje: form.mensaje,
            });
            if (res?.data?.success) {
                showNotification('Solicitud de soporte enviada. Te contactaremos pronto.', 'success');
            } else {
                showNotification(res?.data?.message || 'No se pudo enviar la solicitud. Intenta de nuevo.', 'error');
            }
        } catch (err) {
            console.error('Soporte: error al enviar', err);
            showNotification('No se pudo enviar la solicitud. Intenta más tarde.', 'error');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="help-page">
            <Link to="/configuraciones" className="back-button">
                <FaArrowLeft /> Volver
            </Link>
            <div className="help-hero">
                <div className="help-hero-icon"><FaLifeRing /></div>
                <div>
                    <h2>Contactar a Soporte</h2>
                    <p>Elige el canal más conveniente y envía tu solicitud con el detalle del problema.</p>
                </div>
            </div>

            <div className="help-card contact-grid">
                <div className="contact-method">
                    <div className="contact-icon phone"><FaPhoneAlt /></div>
                    <div className="contact-details">
                        <h4>Teléfono</h4>
                        <p>Atención inmediata para urgencias.</p>
                        <strong>+52 414 226 3804</strong>
                    </div>
                </div>
                <div className="contact-method">
                    <div className="contact-icon mail"><FaEnvelope /></div>
                    <div className="contact-details">
                        <h4>Correo electrónico</h4>
                        <p>Respuesta en horario hábil.</p>
                        <strong>domushabitat2025@gmail.com</strong>
                    </div>
                </div>
            </div>

            <div className="help-card">
                <h3 className="form-title">Enviar solicitud de soporte</h3>
                <form className="support-form" onSubmit={handleSubmit}>
                    <div className="form-row">
                        <div className="form-group">
                            <label htmlFor="nombre">Nombre completo *</label>
                            <input id="nombre" name="nombre" value={form.nombre} onChange={handleChange('nombre')} placeholder="Ej: Juan Pérez" autoCorrect="on" spellCheck="true" autoCapitalize="words" />
                        </div>
                        <div className="form-group">
                            <label htmlFor="correo">Correo electrónico *</label>
                            <input id="correo" name="correo" type="email" value={form.correo} onChange={handleChange('correo')} placeholder="tu@email.com" />
                        </div>
                    </div>

                    <div className="form-row">
                        <div className="form-group">
                            <label htmlFor="telefono">Teléfono</label>
                            <input id="telefono" name="telefono" value={form.telefono} onChange={handleChange('telefono')} placeholder="(755) 123 4567" />
                        </div>
                        <div className="form-group">
                            <label htmlFor="fraccionamiento">Fraccionamiento</label>
                            <input id="fraccionamiento" name="fraccionamiento" value={form.fraccionamiento} onChange={handleChange('fraccionamiento')} placeholder="Ej: Vista Hermosa" />
                        </div>
                    </div>

                    <div className="form-row">
                        <div className="form-group">
                            <label htmlFor="categoria">Categoría del problema *</label>
                            <select id="categoria" name="categoria" value={form.categoria} onChange={handleChange('categoria')}>
                                <option value="">Selecciona una categoría</option>
                                {categories.map((cat) => (
                                    <option key={cat} value={cat}>{cat}</option>
                                ))}
                            </select>
                        </div>
                        <div className="form-group">
                            <label htmlFor="asunto">Asunto *</label>
                            <input id="asunto" name="asunto" value={form.asunto} onChange={handleChange('asunto')} placeholder="Describe brevemente tu consulta" autoCorrect="on" spellCheck="true" autoCapitalize="sentences" />
                        </div>
                    </div>

                    <div className="form-row">
                        <div className="form-group full">
                            <label htmlFor="mensaje">Mensaje *</label>
                            <textarea id="mensaje" name="mensaje" rows="4" value={form.mensaje} onChange={handleChange('mensaje')} placeholder="Incluye detalles, pantallas o folios de pago si aplica." autoCorrect="on" spellCheck="true" autoCapitalize="sentences" />
                        </div>
                    </div>

                    <div className="form-actions">
                        <button type="submit" className="primary" disabled={submitting}>
                            {submitting ? 'Enviando…' : 'Enviar solicitud'}
                        </button>
                    </div>
                </form>

                <div className="schedule">
                    <div className="schedule-icon"><FaClock /></div>
                    <div>
                        <p><strong>Horarios de atención</strong></p>
                        <ul>
                            <li>Lunes a Viernes: 9:00 AM - 6:00 PM</li>
                            <li>Sábados: 9:00 AM - 2:00 PM</li>
                            <li>Domingos y festivos: atención a urgencias por teléfono</li>
                        </ul>
                    </div>
                </div>

                <div className="trust">
                    <FaCheckCircle />
                    <p>Tu solicitud se registra en nuestro sistema y se deriva al área adecuada. Incluye datos claros para acelerar la respuesta.</p>
                </div>
            </div>
        </div>
    );
};

export default Soporte;
